import { mdiGavel, mdiSyncAlert } from '@mdi/js';

import { stableStringify } from '../stable-stringify';

export type AutomationWorkerId = 'rejection' | 'denial';
export type RuleActionType =
    | 'CLOSE_OUT'
    | 'SWITCH_TO_PRIVATE_PAY'
    | 'CREATE_APPEAL'
    | 'CREATE_MESSAGE';

/**
 * Extensible per-match scope.
 * Known values: TRANSMISSION_STATUS, PAYMENT, PAYER, BILLING_PROVIDER,
 * RENDERING_PROVIDER, FACILITY, PROCEDURE_CODE, DATE_OF_SERVICE.
 */
export type RuleMatchScope = string;

/** Worker default scope used when seeding new rules/match blocks. */
export type WorkerDefaultScope = 'TRANSMISSION_STATUS' | 'PAYMENT';

export type ConditionKind =
    | 'billingProviderId'
    | 'carcCode'
    | 'carcGroup'
    | 'contains'
    | 'facilityId'
    | 'payerId'
    | 'procedureCode'
    | 'rarcCode'
    | 'renderingProviderId';

export interface TransmissionMatchValue {
    contains: string[];
}

export interface PaymentMatchValue {
    carc: { codes: string[]; group: string[] };
    rarc: { codes: string[] };
}

export interface PayerMatchValue {
    payerIds: string[];
}

export interface BillingProviderMatchValue {
    billingProviderIds: string[];
}

export interface RenderingProviderMatchValue {
    renderingProviderIds: string[];
}

export interface FacilityMatchValue {
    facilityIds: string[];
}

export interface ProcedureCodeMatchValue {
    procedureCodes: string[];
}

export interface DateOfServiceMatchValue {
    fromDos: string | null;
    throughDos: string | null;
}

export type RuleMatchValue =
    | BillingProviderMatchValue
    | DateOfServiceMatchValue
    | FacilityMatchValue
    | PaymentMatchValue
    | PayerMatchValue
    | ProcedureCodeMatchValue
    | RenderingProviderMatchValue
    | TransmissionMatchValue;

export interface RuleMatch {
    scope: RuleMatchScope;
    value: RuleMatchValue;
}

export interface ReconsiderationDocument {
    documentId: string;
    name: string;
}

export interface RuleAction {
    type: RuleActionType;
    value: Record<string, unknown>;
}

export interface AutomationRule {
    action: RuleAction;
    id: string;
    match: RuleMatch[];
    name: string;
    priority?: number;
}

export interface AutomationWorker {
    active: boolean;
    desc: string;
    icon: string;
    id: AutomationWorkerId;
    key: string;
    name: string;
    rules: AutomationRule[];
    scope: WorkerDefaultScope;
    scopeLabel: string;
}

export interface CatalogCode {
    code: string;
    count?: number;
    desc: string;
}

export interface CatalogGroup {
    count: number;
    group: string;
}

export interface CatalogPayer {
    meta: string;
    name: string;
    payerId: string;
}

export interface CatalogProvider {
    meta: string;
    name: string;
    providerId: string;
}

export interface CatalogFacility {
    facilityId: string;
    meta: string;
    name: string;
}

export interface CatalogDocument {
    documentId: string;
    name: string;
}

export interface EnterCatalog {
    carcCodes: CatalogCode[];
    carcGroups: CatalogGroup[];
    documents: CatalogDocument[];
    payers: CatalogPayer[];
    rarcCodes: CatalogCode[];
    /** Transmission status messages from reporting (message + occurrence count). */
    rejectionStatuses: CatalogCode[];
}

export interface CatalogUsage {
    count: number;
    ruleNames: string[];
    ruleNumbers: number[];
}

export interface UsageBadgePresentation {
    label: string;
    notUsed: boolean;
    short: string;
    title: string;
    used: boolean;
}

export const MESSAGE_CATEGORIES = [
    'PAYER_FOLLOWUP',
    'PROVIDER_FOLLOWUP',
    'CODING_REVIEW',
    'RECORDS_REQUEST',
    'AUTHORIZATION',
    'GENERAL',
] as const;

export type MessageCategory = (typeof MESSAGE_CATEGORIES)[number];

export const AUTOMATION_WORKER_ICONS: Record<AutomationWorkerId, string> = {
    rejection: mdiSyncAlert,
    denial: mdiGavel,
};

export function cloneAutomationWorkers(
    workers: Record<AutomationWorkerId, AutomationWorker>,
): Record<AutomationWorkerId, AutomationWorker> {
    return JSON.parse(JSON.stringify(workers)) as Record<AutomationWorkerId, AutomationWorker>;
}

export function automationWorkersEqual(
    left: Record<AutomationWorkerId, AutomationWorker>,
    right: Record<AutomationWorkerId, AutomationWorker>,
): boolean {
    return stableStringify(left) === stableStringify(right);
}
